import { Skeleton } from '@agro1desenvolvimento/agro1-design-system/components/ui';
import { createFileRoute, useNavigate } from '@tanstack/react-router';
import { useTranslation } from 'react-i18next';
import { z } from 'zod';

import { AppTopbar } from '@/components/app-topbar';
import { PageLayout } from '@/components/page-layout';
import { UnsavedChangesDialog } from '@/components/unsaved-changes-dialog';
import {
  GERMINATION_TEST_ROUTES,
  toGerminationTestKind,
} from '@/modules/germination-test/utils/test-kind';
import { InstallationForm } from '@/modules/installation/components/installation-form';
import { InstallationSamplesCard } from '@/modules/installation/components/installation-samples-card';
import {
  type InstallationDetail,
  type InstallationMode,
} from '@/modules/installation/services/installation-types';
import { type InstallationSaveOutcome } from '@/modules/installation/utils/installation-form-validation';
import { useInstallationDetailViewModel } from '@/modules/installation/view-models/use-installation-detail-view-model';
import { ProducerSelect } from '@/modules/lab-shared/components/producer-select';
import { SyncFailureNotice } from '@/modules/lab-shared/components/sync-failure-notice';
import { analysisDetailSearchSchema, toListSearch } from '@/modules/lab-shared/lib/list-search';

const installationSearchSchema = analysisDetailSearchSchema.extend({
  ids: z.string().catch('').default(''),
});

const BREADCRUMB_KEYS: Record<InstallationMode, string> = {
  install: 'installation.form.title',
  view: 'installation.form.title',
};

function parseIds(value: string): string[] {
  return value.split(',').filter(Boolean);
}

function InstallationPage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { ids: idsParam, ...listSearch } = Route.useSearch();
  const ids = parseIds(idsParam);
  const {
    details,
    mode,
    isBatch,
    hasProducer,
    isLoading,
    error,
    isSaving,
    save,
    readHasUnsavedChanges,
    setHasUnsavedChanges,
  } = useInstallationDetailViewModel(ids);

  const goBack = () =>
    void navigate({ to: '/seeds', search: toListSearch(listSearch, 'instalacao') });

  const showSamples = (nextIds: string[]) =>
    void navigate({
      to: '/seeds/installation',
      search: { ...listSearch, ids: nextIds.join(',') },
      replace: true,
      ignoreBlocker: true,
    });

  const removeSample = (id: string) => showSamples(ids.filter((current) => current !== id));

  const openTestAnalysis = (detail: InstallationDetail) => {
    const params = { id: detail.id };

    if (detail.determination === 'TZ') {
      void navigate({ to: '/seeds/tetrazolium/$id', params, search: { tab: 'tetrazolio' } });
      return;
    }

    const kind = toGerminationTestKind(detail.determination);

    if (!kind) {
      goBack();
      return;
    }

    const { path, tab } = GERMINATION_TEST_ROUTES[kind];

    void navigate({ to: path, params, search: { tab, setup: true } });
  };

  const continueAfterSave = (outcome: InstallationSaveOutcome) => {
    const [single] = details;
    const installed = outcome === 'installation' || outcome === 'treatmentAndInstallation';
    const opensTestAnalysis = !isBatch && installed;

    if (opensTestAnalysis && single) {
      openTestAnalysis(single);
      return;
    }

    goBack();
  };

  const topbar = (
    <AppTopbar
      actions={<ProducerSelect />}
      breadcrumb={t(BREADCRUMB_KEYS[mode])}
      title={t('lab.title')}
      onTitleClick={goBack}
    />
  );

  if (!hasProducer) {
    return (
      <PageLayout>
        {topbar}
        <p className="py-10 text-center text-sm text-muted-foreground">
          {t('lab.noProducerSelected')}
        </p>
      </PageLayout>
    );
  }

  if (isLoading) {
    return (
      <PageLayout>
        <Skeleton className="h-8 w-64" />
        <Skeleton className="h-40 w-full" />
      </PageLayout>
    );
  }

  const [first] = details;
  const removableSample = mode === 'install' ? removeSample : undefined;

  if (error || !first) {
    return (
      <PageLayout>
        {topbar}
        <p className="text-destructive py-10 text-center text-sm">
          {t('installation.form.loadError')}
        </p>
      </PageLayout>
    );
  }

  return (
    <PageLayout>
      {topbar}

      <SyncFailureNotice errors={isBatch ? null : first.syncErrors} />

      <InstallationSamplesCard
        details={details}
        status={first.stageStatus}
        onRemove={removableSample}
      />

      <InstallationForm
        key={`${mode}:${idsParam}`}
        details={details}
        mode={mode}
        submitting={isSaving}
        onCancel={goBack}
        onDirtyChange={setHasUnsavedChanges}
        onSubmit={(input) =>
          save(input, { onSaved: continueAfterSave, onPartialFailure: showSamples })
        }
      />

      <UnsavedChangesDialog when={readHasUnsavedChanges} />
    </PageLayout>
  );
}

export const Route = createFileRoute('/seeds/installation')({
  validateSearch: installationSearchSchema,
  component: InstallationPage,
});
